import {
  getVisibleApps,
  type UiProductType,
} from '@app/app-updates-product-app';
import {
  STATUS_IDS,
  CHANGE_TYPE_IDS,
  IMPACT_RATING_DISPLAY_NAMES,
  IMPACT_RATING_IDS,
  TAG_IDS,
} from '../constants';
import { UpdateStatus, ChangeType, Product, ImpactRating, Tag } from '../types';

export interface FilterUrlParams {
  search?: string;
  products?: string;
  statuses?: string;
  changeTypes?: string;
  impactRatings?: string;
  tags?: string;
  bundleDate?: string;
  cursor?: string;
  limit?: string;
}

export interface FilterState {
  selectedProducts: Product[] | null;
  selectedStatuses: UpdateStatus[] | null;
  selectedChangeTypes: ChangeType[] | null;
  selectedImpactRatings: ImpactRating[] | null;
  selectedTags: Tag[] | null;
  selectedBundleDate?: string | null;
  searchQuery: string | null;
  cursor?: string;
  limit?: number;
}

/**
 * Converts filter state to URL query parameters
 */
export const filtersToUrlParams = (
  filters: Partial<FilterState>,
): FilterUrlParams => {
  const params: FilterUrlParams = {};

  if (filters.searchQuery) {
    params.search = filters.searchQuery;
  }

  if (filters.selectedProducts && filters.selectedProducts.length > 0) {
    params.products = filters.selectedProducts.map((p) => p.id).join(',');
  }

  if (filters.selectedStatuses && filters.selectedStatuses.length > 0) {
    params.statuses = filters.selectedStatuses.map((p) => p.id).join(',');
  }

  if (filters.selectedChangeTypes && filters.selectedChangeTypes.length > 0) {
    params.changeTypes = filters.selectedChangeTypes.map((p) => p.id).join(',');
  }

  if (
    filters.selectedImpactRatings &&
    filters.selectedImpactRatings.length > 0
  ) {
    params.impactRatings = filters.selectedImpactRatings
      .map((p) => p.id)
      .join(',');
  }

  if (filters.selectedTags && filters.selectedTags.length > 0) {
    params.tags = filters.selectedTags.map((p) => p.id).join(',');
  }

  if (filters.selectedBundleDate) {
    params.bundleDate = filters.selectedBundleDate;
  }

  if (filters.cursor) {
    params.cursor = filters.cursor;
  }

  if (typeof filters.limit === 'number' && Number.isFinite(filters.limit)) {
    params.limit = String(filters.limit);
  }

  return params;
};

// Product.id is UiProductType (e.g. 'JIRA_SOFTWARE') for consistent client-side representation.
// getVisibleApps() already returns keys as UiProductType (uppercase).
export const availableProducts: Product[] = Object.entries(
  getVisibleApps(),
).map(([key, config]) => ({
  id: key as UiProductType,
  name: config.displayName,
  icon: config.icon,
}));

const availableStatuses: UpdateStatus[] = STATUS_IDS.map((statusId) => ({
  id: statusId,
  name: statusId,
}));

const availableChangeTypes: ChangeType[] = CHANGE_TYPE_IDS.map(
  (changeTypeId) => ({
    id: changeTypeId,
    name: changeTypeId,
  }),
);

const availableImpactRatings: ImpactRating[] = IMPACT_RATING_IDS.map(
  (ratingId) => ({
    id: ratingId,
    name: IMPACT_RATING_DISPLAY_NAMES[ratingId],
  }),
);

const availableTags: Tag[] = TAG_IDS.map((tagId) => ({
  id: tagId,
  name: tagId,
}));

/**
 * Converts URL query parameters to filter state
 */
export const urlParamsToFilters = (): Partial<FilterState> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);

  const filters: Partial<FilterState> = {};

  const search = params.get('search');
  if (search) {
    filters.searchQuery = search;
  }

  const products = params.get('products');
  if (products) {
    const productKeys = products.split(',').filter(Boolean);
    filters.selectedProducts = availableProducts.filter((p) =>
      productKeys.includes(p.id),
    );
  }

  const statuses = params.get('statuses');
  if (statuses) {
    const statusIds = statuses.split(',').filter(Boolean);
    filters.selectedStatuses = availableStatuses.filter((s) =>
      statusIds.includes(s.id),
    );
  }

  const changeTypes = params.get('changeTypes');
  if (changeTypes) {
    const changeTypeIds = changeTypes.split(',').filter(Boolean);
    filters.selectedChangeTypes = availableChangeTypes.filter((c) =>
      changeTypeIds.includes(c.id),
    );
  }

  const impactRatings = params.get('impactRatings');
  if (impactRatings) {
    const ratingIds = impactRatings.split(',').filter(Boolean);
    filters.selectedImpactRatings = availableImpactRatings.filter((r) =>
      ratingIds.includes(r.id),
    );
  }

  const tags = params.get('tags');
  if (tags) {
    const tagIds = tags.split(',').filter(Boolean);
    filters.selectedTags = availableTags.filter((t) => tagIds.includes(t.id));
  }

  const bundleDate = params.get('bundleDate');
  if (bundleDate) {
    filters.selectedBundleDate = bundleDate;
  }

  const cursor = params.get('cursor');
  if (cursor) {
    filters.cursor = cursor;
  }

  const limit = params.get('limit');
  if (limit) {
    const parsedLimit = Number(limit);
    if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
      filters.limit = parsedLimit;
    }
  }

  return filters;
};

/**
 * Creates a clean URL search string from filter params
 */
export const createFilterUrlSearch = (params: FilterUrlParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const searchString = searchParams.toString();
  return searchString ? `?${searchString}` : '';
};
